import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import type {
  GradeSectionSelection,
  SectionName,
} from "../../../services/teacherPracticeSession";

const SECTIONS: SectionName[] = ["A", "B", "C", "D", "E"];

type Props = {
  isOpen: boolean;
  gradeOptions: number[];
  initialSelection: GradeSectionSelection;
  onClose: () => void;
  onStartPractice: (selection: GradeSectionSelection) => void;
};

const buildEmptySelection = (grades: number[]): GradeSectionSelection =>
  grades.reduce((acc, grade) => {
    acc[grade] = [];
    return acc;
  }, {} as GradeSectionSelection);

const normalizeSections = (sections: SectionName[]): SectionName[] =>
  SECTIONS.filter((section) => sections.includes(section));

const PracticeSessionSetup = ({
  isOpen,
  gradeOptions,
  initialSelection,
  onClose,
  onStartPractice,
}: Props) => {
  const [selection, setSelection] = useState<GradeSectionSelection>({});
  const [openGrade, setOpenGrade] = useState<number | null>(null);
  const [error, setError] = useState("");

  const grades = useMemo(() => [...gradeOptions].sort((a, b) => a - b), [gradeOptions]);

  useEffect(() => {
    if (!isOpen) return;

    const normalized = buildEmptySelection(grades);
    grades.forEach((grade) => {
      normalized[grade] = normalizeSections(initialSelection[grade] ?? []);
    });

    setSelection(normalized);
    setOpenGrade(null);
    setError("");
  }, [grades, initialSelection, isOpen]);

  if (!isOpen) return null;

  const getSummary = (grade: number): string => {
    const sections = selection[grade] ?? [];
    if (sections.length === 0) return "Selected: None";
    if (sections.length === SECTIONS.length) return "Selected: All";
    return `Selected: ${sections.map((section) => `${grade}-${section}`).join(", ")}`;
  };

  const toggleAll = (grade: number): void => {
    setSelection((prev) => {
      const current = prev[grade] ?? [];
      const next = current.length === SECTIONS.length ? [] : [...SECTIONS];
      return { ...prev, [grade]: next };
    });
  };

  const toggleSection = (grade: number, section: SectionName): void => {
    setSelection((prev) => {
      const current = prev[grade] ?? [];
      const isSelected = current.includes(section);
      const next = isSelected
        ? current.filter((entry) => entry !== section)
        : [...current, section];

      return { ...prev, [grade]: normalizeSections(next) };
    });
  };

  const handleClearAll = () => {
    setSelection(buildEmptySelection(grades));
    setOpenGrade(null);
    setError("");
  };

  const handleStart = () => {
    const hasAnySelection = grades.some((grade) => (selection[grade] ?? []).length > 0);
    if (!hasAnySelection) {
      setError("Please select at least one section to start practice.");
      return;
    }

    setError("");
    onStartPractice(selection);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="relative w-full max-w-xl rounded-3xl border border-[#edcdf6] bg-[linear-gradient(180deg,#fff9ff_0%,#f8ebff_100%)] p-5 shadow-2xl">
        <h2 className="text-center text-2xl font-extrabold text-[#5c2f8e]">
          Practice Session Setup
        </h2>

        <div className="mt-4 rounded-2xl border-2 border-[#f0d462] bg-white/70 p-3">
          {grades.map((grade) => {
            const selectedSections = selection[grade] ?? [];
            const isAllSelected = selectedSections.length === SECTIONS.length;
            const isOpenForGrade = openGrade === grade;

            return (
              <div key={grade} className="mb-3 last:mb-0">
                <p className="mb-1 text-sm font-bold text-[#6f3aa1]">Grade {grade}</p>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenGrade((prev) => (prev === grade ? null : grade))}
                    className="flex w-full items-center justify-between rounded-lg border border-[#dfc8f2] bg-white px-3 py-2 text-left text-sm text-[#5f4786]"
                  >
                    <span className="truncate">Select Section(s) | {getSummary(grade)}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isOpenForGrade ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isOpenForGrade && (
                    <div className="absolute z-20 mt-1 w-full rounded-lg border border-[#d5b6f0] bg-white shadow-lg">
                      <button
                        type="button"
                        onClick={() => toggleAll(grade)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#5f4786] hover:bg-[#f6edff] ${
                          isAllSelected ? "bg-[#eee0ff] text-[#4b2f74]" : ""
                        }`}
                      >
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                            isAllSelected
                              ? "border-[#7f53bb] bg-[#7f53bb] text-white"
                              : "border-[#bca1d8]"
                          }`}
                        >
                          {isAllSelected ? <Check className="h-3 w-3" /> : null}
                        </span>
                        All
                      </button>

                      {SECTIONS.map((section) => {
                        const checked = selectedSections.includes(section);
                        return (
                          <button
                            key={`${grade}-${section}`}
                            type="button"
                            onClick={() => toggleSection(grade, section)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#5f4786] hover:bg-[#f6edff] ${
                              checked ? "bg-[#eee0ff] text-[#4b2f74]" : ""
                            }`}
                          >
                            <span
                              className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                                checked
                                  ? "border-[#7f53bb] bg-[#7f53bb] text-white"
                                  : "border-[#bca1d8]"
                              }`}
                            >
                              {checked ? <Check className="h-3 w-3" /> : null}
                            </span>
                            {grade}-{section}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-3 text-center text-sm text-[#7d659f]">
          Select one or more sections from any grade to practice.
        </p>

        {error && (
          <div className="mt-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-[#8c3db5] px-3 py-2 text-sm font-bold text-white hover:brightness-105"
          >
            &larr; Go Back to Dashboard
          </button>
          <button
            type="button"
            onClick={handleStart}
            className="rounded-xl bg-gradient-to-r from-[#36be67] to-[#24a85b] px-3 py-2 text-sm font-bold text-white hover:brightness-105"
          >
            Start Practice
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className="rounded-xl bg-[#6abce5] px-3 py-2 text-sm font-bold text-white hover:brightness-105"
          >
            Clear All
          </button>
        </div>

        <button
          type="button"
          aria-label="Close setup"
          onClick={onClose}
          className="absolute right-6 top-6 rounded-full p-1 text-[#9b7ec1] hover:bg-white/70"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PracticeSessionSetup;
